import type {ScriptBlockData} from '../components/story/ScriptBlock';
import type {AutocompleteItem} from '../components/story/AutocompleteMenu';

export type CompletionStage = 'sceneIntros' | 'locations' | 'timesOfDay' | 'characters' | 'extensions' | 'transitions';
export interface ScriptCompletionLists {
    sceneIntros: AutocompleteItem[]; locations: AutocompleteItem[];
    timesOfDay: AutocompleteItem[]; characters: AutocompleteItem[];
    extensions:AutocompleteItem[];transitions:AutocompleteItem[];
    introSeparator: string; timeSeparator: string;
}
export type CompletionSource = Partial<ScriptCompletionLists>;
export interface CompletionContext {stage:CompletionStage; start:number; end:number; query:string}
// Data from the installed FD11 Screenplay.fdxt, not a recovered matcher.
const extensions=['(V.O.)','(O.S.)','(O.C.)',"(CONT'D)",'(SUBTITLE)'];
const transitions=['CUT TO:','FADE IN:','FADE OUT.','FADE TO:','DISSOLVE TO:','BACK TO:',
    'MATCH CUT TO:','JUMP CUT TO:','FADE TO BLACK.','SMASH CUT TO:','CUT TO BLACK.','TIME CUT:'];
const intros = ['INT','EXT','I/E'];
const times = ['DAY','NIGHT','AFTERNOON','MORNING','EVENING','LATER','MOMENTS LATER',
    'CONTINUOUS','THE NEXT DAY','MAGIC HOUR','DAWN','DUSK','SAME','SAME TIME'];
const items = (values:readonly string[]) => values.map(label=>({label}));
function unique(values:readonly AutocompleteItem[]):AutocompleteItem[] {
    const result=new Map<string,AutocompleteItem>();
    for(const item of values){const label=item.label.trim().toUpperCase();if(label&&!result.has(label))result.set(label,{...item,label});}
    return [...result.values()];
}

/** Read preserved imported XML without changing it or copying private source
 * text into a global completion store. Direct children only. */
export function readFDXCompletionSource(xml?:string):CompletionSource {
    if(!xml || xml.length>5*1024*1024 || /<!DOCTYPE|<!ENTITY/i.test(xml))return {};
    const document=new DOMParser().parseFromString(xml,'application/xml');
    if(document.querySelector('parsererror') || document.documentElement.tagName!=='FinalDraft')return {};
    const smart=[...document.documentElement.children].find(e=>e.tagName==='SmartType');
    if(!smart)return {};
    const result:CompletionSource={};
    for(const [stage,section,entry] of [
        ['sceneIntros','SceneIntros','SceneIntro'],['locations','Locations','Location'],
        ['timesOfDay','TimesOfDay','TimeOfDay'],['characters','Characters','Character'],
        ['extensions','Extensions','Extension'],['transitions','Transitions','Transition'],
    ] as const){
        const list=[...smart.children].find(e=>e.tagName===section);
        if(!list)continue;
        result[stage]=unique([...list.children].filter(e=>e.tagName===entry).map(e=>({label:e.textContent??''})));
        const separator=list.getAttribute('Separator');
        if(separator && separator.length<=16 && !/[\r\n]/.test(separator)){
            if(stage==='sceneIntros')result.introSeparator=separator;
            if(stage==='timesOfDay')result.timeSeparator=separator;
        }
    }
    return result;
}

/** Match fields, never replace a whole heading with another scene's heading. */
export function completionContext(type:ScriptBlockData['type'],text:string,lists:ScriptCompletionLists):CompletionContext|undefined {
    if(type==='character'){
        const extension=text.lastIndexOf(' (');
        if(extension>0)return {stage:'extensions',start:extension+1,end:text.length,query:text.slice(extension+1)};
        return {stage:'characters',start:0,end:text.length,query:text};
    }
    if(type==='transition')return {stage:'transitions',start:0,end:text.length,query:text};
    if(type!=='scene-heading')return;
    const intro=text.indexOf(lists.introSeparator);
    if(intro<0)return {stage:'sceneIntros',start:0,end:text.length,query:text.replace(/\.$/,'')};
    const start=intro+lists.introSeparator.length;
    const time=text.lastIndexOf(lists.timeSeparator);
    if(time>=start)return {stage:'timesOfDay',start:time+lists.timeSeparator.length,end:text.length,query:text.slice(time+lists.timeSeparator.length)};
    return {stage:'locations',start,end:text.length,query:text.slice(start)};
}
export function completionMatches(context:CompletionContext,lists:ScriptCompletionLists,all=false):AutocompleteItem[] {
    const query=context.query.trim().toUpperCase();
    if(!query&&!all)return [];
    return lists[context.stage].filter(item=>item.label.startsWith(query));
}
export function completedField(text:string,context:CompletionContext,item:AutocompleteItem,lists:ScriptCompletionLists,advance:boolean):string {
    const suffix=context.stage==='sceneIntros'?lists.introSeparator:
        context.stage==='locations'&&advance?lists.timeSeparator:'';
    return text.slice(0,context.start)+item.label+suffix+text.slice(context.end);
}
export function sceneTabText(text:string,lists:ScriptCompletionLists):string|undefined {
    const context=completionContext('scene-heading',text,lists)!;
    if(context.stage==='sceneIntros'){
        const intro=lists.sceneIntros.find(item=>item.label===context.query.trim().toUpperCase());
        return intro?completedField(text,context,intro,lists,true):undefined;
    }
    if(context.stage==='locations'&&context.query.trim())return text+lists.timeSeparator;
    return;
}

export function buildCompletionLists(blocks:readonly ScriptBlockData[],projectCharacters:readonly {name:string;id:string}[]=[],source:CompletionSource={}):ScriptCompletionLists {
    const lists:ScriptCompletionLists={sceneIntros:unique(source.sceneIntros?.length?source.sceneIntros:items(intros)),
        locations:unique(source.locations??[]),timesOfDay:unique([...(source.timesOfDay??[]),...items(times)]),
        characters:unique([...projectCharacters.map(c=>({label:c.name,id:c.id})),...(source.characters??[])]),
        extensions:unique([...(source.extensions??[]),...items(extensions)]),
        transitions:unique([...(source.transitions??[]),...items(transitions)]),
        introSeparator:source.introSeparator??'. ',timeSeparator:source.timeSeparator??' - '};
    for(const block of blocks){
        if(block.type==='scene-heading'){
            const ctx=completionContext(block.type,block.content,lists)!;
            if(ctx.stage!=='sceneIntros'){
                const prefix=block.content.indexOf(lists.introSeparator);
                lists.sceneIntros.push({label:block.content.slice(0,prefix)});
                const time=block.content.lastIndexOf(lists.timeSeparator);
                const start=prefix+lists.introSeparator.length;
                lists.locations.push({label:block.content.slice(start,ctx.stage==='timesOfDay'?time:undefined)});
                if(ctx.stage==='timesOfDay')lists.timesOfDay.push({label:ctx.query});
            }
        }
        if(block.type==='transition'&&block.content.trim())lists.transitions.push({label:block.content});
        const cues=block.type==='character'?[block]:block.dualDialogue?[...block.dualDialogue.left,...block.dualDialogue.right].filter(p=>p.type==='character'):[];
        for(const cue of cues)lists.characters.push({label:cue.content.replace(/(?:\s*\([^)]*\))+\s*$/,'').trim()});
    }
    for(const stage of ['sceneIntros','locations','timesOfDay','characters','extensions','transitions'] as const)lists[stage]=unique(lists[stage]);
    lists.locations.sort((a,b)=>a.label.localeCompare(b.label));lists.characters.sort((a,b)=>a.label.localeCompare(b.label));
    return lists;
}

/** Only a literal suffix is previewed; accepting remains an explicit edit. */
export function completionSuffix(context:CompletionContext|undefined,item:AutocompleteItem|undefined):string {
    if(!context||!item)return '';
    const query=context.query.toUpperCase();
    return item.label.startsWith(query)?item.label.slice(query.length):'';
}

/** FD11 manual pp.330/332: repeat the first letter to browse, not edit.
 * Extensions include an opening parenthesis in our stored field syntax.
 * Scene intros are deliberately excluded from the documented list. */
export function cyclesCompletion(context:CompletionContext|undefined,key:string):boolean {
    if(!context||context.stage==='sceneIntros'||!/^\p{L}$/u.test(key))return false;
    const first=context.stage==='extensions'?context.query.replace(/^\(/,''):context.query;
    return /^\p{L}$/u.test(first)&&first.toUpperCase()===key.toUpperCase();
}
